<script setup lang="ts">
import { computed, ref } from "vue";
import { Trash2 } from "lucide-vue-next";
import { useApiStore } from "@/stores/api";
import HydrusRating from "@/components/HydrusRating/index.vue";
import ServiceFormModal from "@/components/rules/ServiceFormModal.vue";
import type { AnyRatingService, AnyRatingServiceIncludingValue, AnyService } from "@/stores/ratingService";
import { isBaseService, RatingServiceType } from "@/stores/ratingService";
import type { ServiceHash } from "@/stores/ImageMetadata";
import type { ServiceData } from "@/components/rules/ServiceFormModal.vue";

const props = defineProps<{
  showManualFormBtn?: boolean;
}>();

const store = useApiStore();

// Drag state
const draggedIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);

// Modal state
const showModal = ref(false);
const editingServiceKey = ref<string | null>(null);

function withNullValue(service: AnyRatingService): AnyRatingServiceIncludingValue {
  return { ...service, value: null } as AnyRatingServiceIncludingValue;
}

function previewValue(service: AnyRatingService): AnyRatingServiceIncludingValue {
  // Counter services preview with 0, others with null
  const value = service.type === RatingServiceType.Counter ? 0 : null;
  return { ...service, value } as AnyRatingServiceIncludingValue;
}

function previewFor(serviceId: ServiceHash): AnyRatingServiceIncludingValue | null {
  // Try availableServices first (bulk fetched), then fallback to services (individual fetches)
  let service = store.availableServices?.[serviceId];
  if (!service && store.services) {
    service = store.services[serviceId];
  }
  if (!service) return null;
  return withNullValue(service as AnyRatingService);
}

const RATING_TYPES_SET = new Set([RatingServiceType.Stars, RatingServiceType.Like, RatingServiceType.Counter]);

const availableRatingServices = computed<AnyRatingService[] | null>(() => {
  if (!store.availableServices) return null;
  return Object.values(store.availableServices).filter(
    (s) => RATING_TYPES_SET.has(s.type as RatingServiceType) && isBaseService(s)
  ) as AnyRatingService[];
});

const availableToAdd = computed(() => {
  if (!availableRatingServices.value) return [];
  return availableRatingServices.value.filter((s) => !store.voteServiceIds.includes(s.service_key));
});

// Drag handlers
function onDragStart(index: number) {
  draggedIndex.value = index;
}

function onDragOver(index: number) {
  dragOverIndex.value = index;
}

function onDragEnd() {
  draggedIndex.value = null;
  dragOverIndex.value = null;
}

function onDrop(dropIndex: number) {
  if (draggedIndex.value === null || draggedIndex.value === dropIndex) {
    onDragEnd();
    return;
  }
  const items = [...store.voteServiceIds];
  const [removed] = items.splice(draggedIndex.value, 1);
  items.splice(dropIndex, 0, removed);
  store.voteServiceIds = items;
  onDragEnd();
}

// Service actions
function editService(serviceId: ServiceHash) {
  editingServiceKey.value = serviceId;
  showModal.value = true;
}

function addService(serviceKey: ServiceHash) {
  store.voteServiceIds = [...store.voteServiceIds, serviceKey];
}

function removeService(serviceId: ServiceHash) {
  store.voteServiceIds = store.voteServiceIds.filter((id) => id !== serviceId);
}

function openAddForm() {
  editingServiceKey.value = null;
  showModal.value = true;
}

function getServiceData(serviceId: ServiceHash): ServiceData | null {
  const service = store.availableServices?.[serviceId] || store.services?.[serviceId];
  if (!service) return null;
  return {
    service_key: service.service_key,
    name: service.name,
    type: (service.type as RatingServiceType) || RatingServiceType.Stars,
    min_stars:
      ("min_stars" in service && service.min_stars !== undefined ? (service.min_stars as 0 | 1) : undefined),
    max_stars: ("max_stars" in service && service.max_stars !== undefined ? (service.max_stars as number) : undefined),
    star_shape: ("star_shape" in service && service.star_shape ? (service.star_shape as any) : undefined),
  };
}

function onModalSave(data: ServiceData) {
  const isEditing = editingServiceKey.value !== null;
  const oldKey = editingServiceKey.value ?? "";
  const newKey = data.service_key;

  const service: Record<string, unknown> = {
    service_key: newKey,
    name: data.name,
    type: data.type,
    type_pretty:
      data.type === RatingServiceType.Stars
        ? "manual stars rating"
        : data.type === RatingServiceType.Like
          ? "manual like/dislike rating"
          : "manual counter rating",
  };

  if (data.min_stars !== undefined) service.min_stars = data.min_stars;
  if (data.max_stars !== undefined) service.max_stars = data.max_stars;
  if (data.star_shape !== undefined) service.star_shape = data.star_shape;

  // Update stores
  if (!store.availableServices) {
    store.availableServices = {};
  }

  const newAvailable = { ...store.availableServices };
  if (isEditing && oldKey && oldKey !== newKey && oldKey in newAvailable) {
    delete newAvailable[oldKey];
  }
  newAvailable[newKey] = service as AnyService;
  store.availableServices = newAvailable;

  // Update voteServiceIds if editing and key changed, or if adding new
  if (isEditing && oldKey && oldKey !== newKey) {
    store.voteServiceIds = store.voteServiceIds.map((id) => (id === oldKey ? newKey : id));
  } else if (!isEditing && !store.voteServiceIds.includes(newKey)) {
    store.voteServiceIds = [...store.voteServiceIds, newKey];
  }

  showModal.value = false;
  editingServiceKey.value = null;
}

function onModalClose() {
  showModal.value = false;
  editingServiceKey.value = null;
}
</script>

<template>
  <div class="vote-service-editor">
    <!-- Configured services (draggable) -->
    <div class="section">
      <h4 class="section-title">Configured — drag to reorder</h4>
      <div class="vote-service-editor__list">
        <p v-if="store.voteServiceIds.length === 0" class="vote-service-editor__empty">
          No vote services configured.
        </p>
        <div
          v-for="(serviceId, index) in store.voteServiceIds"
          :key="serviceId"
          class="vote-service-editor__item"
          :class="{ dragging: draggedIndex === index, 'drop-target': dragOverIndex === index }"
          draggable="true"
          @dragstart="onDragStart(index)"
          @dragover.prevent="onDragOver(index)"
          @drop="onDrop(index)"
          @dragend="onDragEnd"
          @dragleave="dragOverIndex = null"
          @click.prevent="draggedIndex === null && editService(serviceId)"
          role="button"
          :tabindex="draggedIndex === null ? 0 : -1"
        >
          <div class="item-content">
            <div class="item-preview">
              <HydrusRating
                v-if="previewFor(serviceId)"
                :rating="previewFor(serviceId)!"
                :show-buttons="false"
              />
              <span v-else class="vote-service-editor__id-fallback">—</span>
            </div>
            <div class="item-info">
              <div class="item-name">
                {{
                  (store.availableServices?.[serviceId]?.name || store.services?.[serviceId]?.name) || "Unknown"
                }}
              </div>
              <div class="item-id">{{ serviceId.slice(0, 16) }}</div>
            </div>
          </div>
          <button
            v-if="draggedIndex !== index"
            class="btn-remove-service"
            title="Remove service"
            @click.stop="removeService(serviceId)"
          >
            <Trash2 :size="14" />
          </button>
        </div>
      </div>
    </div>

    <!-- Available services pool -->
    <div v-if="availableRatingServices !== null" class="section">
      <h4 class="section-title">Available — click to add</h4>
      <div v-if="availableToAdd.length === 0" class="pool-empty">
        All available services are already configured.
      </div>
      <div v-else class="pool-services">
        <button
          v-for="service in availableToAdd"
          :key="service.service_key"
          class="pool-option"
          @click="addService(service.service_key)"
        >
          <HydrusRating :rating="previewValue(service)" :show-buttons="false" />
          <span class="pool-option__name">{{ service.name }}</span>
        </button>
      </div>
    </div>

    <!-- Add/Edit form button -->
    <div
      v-if="
        props.showManualFormBtn !== false &&
        (store.availableServices === null || props.showManualFormBtn === true || editingServiceKey)
      "
      class="section"
    >
      <button v-if="!showModal" class="btn-add-form" @click="openAddForm">
        +
        {{ editingServiceKey ? "Edit" : "Add manually…" }}
      </button>
    </div>

    <!-- Hint when services not loaded -->
    <div v-if="availableRatingServices === null" class="hint-box">
      💡 Click 'Refresh from API' in the configure panel to load available services from Hydrus
    </div>

    <!-- Service form modal -->
    <ServiceFormModal
      :open="showModal"
      :is-editing="editingServiceKey !== null"
      :initial-data="editingServiceKey ? getServiceData(editingServiceKey as ServiceHash) : null"
      :show-warning="editingServiceKey === null"
      @save="onModalSave"
      @close="onModalClose"
    />
  </div>
</template>

<style scoped lang="scss">
.vote-service-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-title {
  margin: 0;
  font-size: 0.9em;
  font-weight: 500;
  color: var(--text-secondary, #888);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.vote-service-editor__list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.vote-service-editor__empty,
.pool-empty {
  margin: 0;
  font-size: 0.85em;
  color: var(--text-secondary, #888);
  font-style: italic;
}

.vote-service-editor__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: var(--bg-elevated);
  border: 2px solid var(--border);
  border-radius: var(--radius-md);
  cursor: grab;
  transition: all 0.2s ease;
  user-select: none;

  &:hover:not(.dragging) {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 5%, var(--bg-elevated));
  }

  &:active:not(.dragging) {
    opacity: 0.9;
  }

  &.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  &.drop-target {
    background: color-mix(in srgb, var(--accent) 10%, var(--bg-elevated));
    border-color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
}

.item-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.item-preview {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  min-width: 32px;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.item-name {
  font-size: 0.9em;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-id {
  font-size: 0.75em;
  color: var(--text-secondary, #888);
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-remove-service {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted, #888);
  padding: 2px;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--danger, #e55);
  }
}

.pool-services {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pool-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 6px 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: var(--bg-elevated);
    border-color: var(--accent);
  }
}

.pool-option__name {
  font-size: 0.88em;
  color: var(--text-primary);
}

.btn-add-form {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm);
  padding: 6px 12px;
  cursor: pointer;
  color: var(--text-secondary, var(--text-primary));
  font-size: 0.85em;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.hint-box {
  padding: 8px 10px;
  background: color-mix(in srgb, var(--accent) 10%, var(--bg-surface));
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.8em;
  color: var(--text-secondary);
  line-height: 1.4;
}
</style>
